import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { tripService } from '../../services/tripService';

import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import MobileNavigation from '../../components/navigation/MobileNavigation';
import TripInfoTab from './components/TripInfoTab';
import StorageTab from './components/StorageTab';
import EquipmentTab from './components/EquipmentTab';
import PreferencesTab from './components/PreferencesTab';
import DataTab from './components/DataTab';
import SubscriptionTab from '../account-settings/components/SubscriptionTab';

const TripSettings = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('trip-info');
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);

  const tabs = [
    { id: 'info', label: 'Trip Information', icon: 'Ship' },
    { id: 'storage', label: 'Storage Configuration', icon: 'Archive' },
    { id: 'equipment', label: 'Cooking Equipment', icon: 'Flame' },
    { id: 'preferences', label: 'Meal Preferences', icon: 'Utensils' },
    { id: 'data', label: 'Trip Data', icon: 'Database' },
    { id: 'subscription', label: 'Subscription & Billing', icon: 'CreditCard' }
  ];

  useEffect(() => {
    loadTripData();
  }, [user]);

  const loadTripData = async () => {
    if (!user?.id) {
      navigate('/login');
      return;
    }

    setLoading(true);

    try {
      const { data: tripData, error: tripError } = await tripService?.getActiveTrip(user?.id);
      
      if (!tripError && tripData) {
        setTrip(tripData);
      } else if (!tripError && !tripData) {
        // No active trip found - redirect to trip setup
        navigate('/trip-setup');
        return;
      }
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // Handle error
    }
  };

  const handleTripUpdate = async (updates) => {
    if (!trip?.id) return;

    const { data, error } = await tripService?.updateTrip(trip?.id, updates);
    
    if (!error && data) {
      setTrip(data);
      return { success: true };
    }
    
    return { success: false, error };
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Trip Settings - GalleyPlan</title>
        </Helmet>
        <Header />
        <div className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-muted-foreground">Loading trip settings...</p>
          </div>
        </div>
        <MobileNavigation />
      </>
    );
  }

  if (!trip) {
    return (
      <>
        <Helmet>
          <title>Trip Settings - GalleyPlan</title>
        </Helmet>
        <Header />
        <div className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-muted-foreground">No active trip found. Please create a trip first.</p>
          </div>
        </div>
        <MobileNavigation />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Trip Settings - GalleyPlan</title>
        <meta name="description" content="Configure your coastal cruise trip settings and preferences" />
      </Helmet>
      <Header />
      <div className="min-h-screen pt-16 pb-20 md:pb-8">
        <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Trip Settings
            </h1>
            <p className="mt-2 text-muted-foreground">
              Configure your trip parameters and meal planning preferences
            </p>
          </div>

          {/* Tabs Navigation - Desktop */}
          <div className="hidden md:block mb-8">
            <div className="border-b" style={{ borderColor: 'var(--color-border)' }}>
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tabs Navigation - Mobile (Accordion Style) */}
          <div className="md:hidden mb-6">
            <div className="space-y-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-foreground hover:bg-accent'
                  }`}
                  style={activeTab !== tab?.id ? { backgroundColor: 'var(--color-card)' } : {}}
                >
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="rounded-lg p-6" style={{ backgroundColor: 'var(--color-card)' }}>
            {activeTab === 'info' && <TripInfoTab tripData={trip} onUpdate={handleTripUpdate} />}
            {activeTab === 'storage' && <StorageTab tripData={trip} onUpdate={handleTripUpdate} />}
            {activeTab === 'equipment' && <EquipmentTab tripData={trip} onUpdate={handleTripUpdate} />}
            {activeTab === 'preferences' && <PreferencesTab tripData={trip} onUpdate={handleTripUpdate} />}
            {activeTab === 'data' && <DataTab tripData={trip} />}
            {activeTab === 'subscription' && <SubscriptionTab user={user} userProfile={userProfile} />}
          </div>
        </div>
      </div>
      <MobileNavigation />
    </>
  );
};

export default TripSettings;